import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ShieldCheck, Loader2, AlertCircle, Check, Copy,
  KeyRound, RefreshCw, Smartphone, AlertTriangle, Download,
} from "lucide-react";
import SEO from "../components/SEO";
import { useProAuth } from "../components/ProAuthContext";
import { API } from "../apiBase";
export default function MssanteSecurite() {
  const { user, refresh } = useProAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Setup state
  const [setupData, setSetupData] = useState(null); // { qr_data_uri, otpauth_uri, secret }
  const [setupCode, setSetupCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable / regenerate state
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [showDisable, setShowDisable] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/pro/2fa/status`);
      setStatus(data);
    } catch {
      setErr("Impossible de charger le statut 2FA.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/2fa/setup`);
      setSetupData(data);
      setSetupCode("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Initialisation impossible.");
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/2fa/enable`, { code: setupCode.trim() });
      setRecoveryCodes(data.recovery_codes);
      setSetupData(null);
      setSetupCode("");
      await loadStatus();
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Code invalide.");
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/pro/2fa/disable`, { password: disablePwd, code: disableCode.trim() });
      setShowDisable(false);
      setDisablePwd(""); setDisableCode("");
      await loadStatus();
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Désactivation impossible.");
    } finally { setBusy(false); }
  };

  const regenerate = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pro/2fa/recovery/regenerate`, {
        password: disablePwd, code: disableCode.trim(),
      });
      setRecoveryCodes(data.recovery_codes);
      setShowRegenerate(false);
      setDisablePwd(""); setDisableCode("");
      await loadStatus();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Régénération impossible.");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-mssante-securite">
      <SEO title="Sécurité — Espace MetaConnect" canonical="/mssante/espace/securite" noIndex />

      <section className="hex-grid pt-32 pb-10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Link to="/mssante/espace" className="text-[12px] text-[#475569] hover:text-[#0F2B5B] inline-flex items-center gap-1.5">
            <ArrowLeft size={12} /> Retour à mon espace
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-emerald-600">
            <ShieldCheck size={13} /> Sécurité du compte
          </div>
          <h1 className="mt-3 font-display text-[#0F2B5B] text-3xl sm:text-4xl font-semibold leading-tight">
            Authentification à deux facteurs
          </h1>
          <p className="mt-2 text-[13px] text-[#475569] max-w-2xl">
            Protégez votre espace avec un code temporaire généré par une application
            (Google Authenticator, Authy, 1Password…) en plus de votre mot de passe.
          </p>
        </div>
      </section>

      <section className="hex-soft py-8 lg:py-12">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 grid gap-5">
          {loading ? (
            <div className="text-[#475569] text-[13px]">Chargement…</div>
          ) : (
            <>
              {/* Status header */}
              <div className="mc-card p-5 lg:p-6 flex items-center gap-4 flex-wrap" data-testid="sec-status">
                <div className={`grid place-items-center h-12 w-12 rounded-full shrink-0 ${
                  status?.enabled
                    ? "bg-[rgba(110,231,197,0.14)] border border-[var(--mc-mint)] text-emerald-600"
                    : "bg-[rgba(255,180,80,0.10)] border border-[rgba(255,180,80,0.4)] text-[#FFC97D]"
                }`}>
                  {status?.enabled ? <ShieldCheck size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#0F2B5B] font-medium">
                    {status?.enabled ? "2FA activée" : "2FA non activée"}
                  </div>
                  <div className="text-[12.5px] text-[#475569] mt-0.5">
                    {status?.enabled
                      ? `Vos connexions sont protégées par un code à 6 chiffres. ${status.recovery_codes_remaining} code(s) de récupération restant(s).`
                      : "Recommandé pour tous les comptes professionnels manipulant des données de santé."}
                  </div>
                </div>
                {status?.enabled ? (
                  <span className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-[rgba(110,231,197,0.4)] text-emerald-600 shrink-0">
                    Active
                  </span>
                ) : (
                  <span className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-[rgba(255,180,80,0.4)] text-[#FFC97D] shrink-0">
                    Inactive
                  </span>
                )}
              </div>

              {err && <ErrBox>{err}</ErrBox>}

              {/* === Recovery codes display (post-enable or post-regenerate) === */}
              {recoveryCodes && (
                <div className="mc-card p-5 lg:p-6 border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.04)]"
                     data-testid="sec-recovery">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-600 inline-flex items-center gap-2 mb-2">
                    <KeyRound size={12} /> Codes de récupération (à conserver précieusement)
                  </div>
                  <p className="text-[12.5px] text-[#475569] mb-3">
                    Ces 10 codes à usage unique vous permettent de vous connecter si vous perdez votre téléphone.
                    <strong className="text-emerald-600"> Ils ne seront plus affichés ensuite.</strong>
                  </p>
                  <div className="grid grid-cols-2 gap-2 font-mono text-[13px] text-[#0F2B5B] p-3 rounded-lg bg-[#F1F5F9] border border-[rgba(110,231,197,0.2)]">
                    {recoveryCodes.map((c) => (
                      <div key={c} className="px-2 py-1.5 rounded border border-[rgba(127,179,255,0.14)]">{c}</div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button onClick={() => navigator.clipboard?.writeText(recoveryCodes.join("\n"))}
                            data-testid="sec-recovery-copy"
                            className="btn-ghost px-3 py-1.5 rounded-full text-[11.5px] inline-flex items-center gap-1.5">
                      <Copy size={11} /> Copier
                    </button>
                    <button onClick={() => downloadTxt(`metaconnect-2fa-recovery-${user?.email || "codes"}.txt`,
                      `Codes de récupération MetaConnect — ${user?.email}\n\n${recoveryCodes.join("\n")}\n\nGénérés le ${new Date().toLocaleString("fr-FR")}\n`)}
                            data-testid="sec-recovery-download"
                            className="btn-ghost px-3 py-1.5 rounded-full text-[11.5px] inline-flex items-center gap-1.5">
                      <Download size={11} /> Télécharger
                    </button>
                    <button onClick={() => setRecoveryCodes(null)} data-testid="sec-recovery-ack"
                            className="btn-primary px-4 py-1.5 rounded-full text-[11.5px] inline-flex items-center gap-1.5">
                      <Check size={11} /> J'ai sauvegardé mes codes
                    </button>
                  </div>
                </div>
              )}

              {/* === Setup wizard === */}
              {!status?.enabled && setupData && !recoveryCodes && (
                <form onSubmit={enable} className="mc-card p-5 lg:p-6 grid gap-4" data-testid="sec-setup-form">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] inline-flex items-center gap-2">
                    <Smartphone size={12} /> Étape 1/2 — Scannez le QR code
                  </div>
                  <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
                    <div className="rounded-lg p-3 bg-white inline-block">
                      <img src={setupData.qr_data_uri} alt="QR code 2FA" className="w-44 h-44" data-testid="sec-setup-qr" />
                    </div>
                    <div className="text-[12.5px] text-[#475569] grid gap-2">
                      <div>Ouvrez votre application d'authentification (Google Authenticator, Authy, 1Password, Microsoft Authenticator…) et scannez ce QR code.</div>
                      <div>Saisie manuelle (si pas de scan) :</div>
                      <code className="font-mono text-[12px] text-[#0F2B5B] px-3 py-2 rounded bg-[#F1F5F9] border border-slate-200 break-all">
                        {setupData.secret}
                      </code>
                      <div className="text-[11px]">Émetteur : MetaConnect · Compte : {user?.email}</div>
                    </div>
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[#0369A1] mt-2">
                    Étape 2/2 — Entrez le code à 6 chiffres affiché
                  </div>
                  <input data-testid="sec-setup-code"
                         className="mc-input w-full font-mono tracking-[0.4em] text-center text-[18px]"
                         maxLength={6} inputMode="numeric" autoFocus
                         value={setupCode}
                         onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ""))} />
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    <button type="button" onClick={() => { setSetupData(null); setSetupCode(""); }}
                            className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
                    <button type="submit" disabled={busy || setupCode.length !== 6} data-testid="sec-setup-submit"
                            className="btn-primary px-5 py-2 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
                      {busy && <Loader2 size={12} className="animate-spin" />}
                      Activer le 2FA <ShieldCheck size={12} />
                    </button>
                  </div>
                </form>
              )}

              {/* === Activate button (when not enabled, no setup yet) === */}
              {!status?.enabled && !setupData && !recoveryCodes && (
                <div className="mc-card p-5 lg:p-6">
                  <div className="text-[14px] text-[#0F2B5B] font-medium mb-1">Activer le 2FA</div>
                  <p className="text-[12.5px] text-[#475569] mb-4">
                    Vous aurez besoin d'une application d'authentification compatible TOTP (Google Authenticator, Authy, 1Password, Microsoft Authenticator…).
                  </p>
                  <button onClick={startSetup} disabled={busy} data-testid="sec-setup-start"
                          className="btn-primary px-5 py-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
                    {busy && <Loader2 size={12} className="animate-spin" />}
                    <Smartphone size={12} /> Configurer le 2FA
                  </button>
                </div>
              )}

              {/* === Manage when enabled === */}
              {status?.enabled && (
                <div className="grid gap-5">
                  <div className="mc-card p-5 lg:p-6">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="text-[#0F2B5B] font-medium text-[14px] inline-flex items-center gap-2">
                          <RefreshCw size={14} className="text-[#0369A1]" /> Régénérer les codes de récupération
                        </div>
                        <p className="text-[12.5px] text-[#475569] mt-1">
                          Annule les codes existants et en génère 10 nouveaux. Utile si vous pensez qu'ils ont été exposés.
                        </p>
                      </div>
                      {!showRegenerate && (
                        <button onClick={() => { setShowRegenerate(true); setShowDisable(false); setErr(""); }}
                                data-testid="sec-regen-open"
                                className="btn-ghost px-4 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5">
                          <RefreshCw size={12} /> Régénérer
                        </button>
                      )}
                    </div>
                    {showRegenerate && (
                      <form onSubmit={regenerate} className="mt-4 grid gap-3" data-testid="sec-regen-form">
                        <PwdInput value={disablePwd} onChange={setDisablePwd} />
                        <CodeInput value={disableCode} onChange={setDisableCode} />
                        <div className="flex items-center gap-2 justify-end">
                          <button type="button" onClick={() => { setShowRegenerate(false); setDisablePwd(""); setDisableCode(""); setErr(""); }}
                                  className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
                          <button type="submit" disabled={busy || !disablePwd || !disableCode}
                                  data-testid="sec-regen-submit"
                                  className="btn-primary px-4 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60">
                            {busy && <Loader2 size={12} className="animate-spin" />}
                            Régénérer les codes
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  <div className="mc-card p-5 lg:p-6 border-[rgba(255,120,120,0.30)]">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-[220px]">
                        <div className="text-[#0F2B5B] font-medium text-[14px] inline-flex items-center gap-2">
                          <AlertTriangle size={14} className="text-[#FFB4B4]" /> Désactiver le 2FA
                        </div>
                        <p className="text-[12.5px] text-[#475569] mt-1">
                          Votre compte ne sera plus protégé que par votre mot de passe. Non recommandé.
                        </p>
                      </div>
                      {!showDisable && (
                        <button onClick={() => { setShowDisable(true); setShowRegenerate(false); setErr(""); }}
                                data-testid="sec-disable-open"
                                className="btn-ghost px-4 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5 border-[rgba(255,120,120,0.35)] text-[#FFB4B4]">
                          Désactiver
                        </button>
                      )}
                    </div>
                    {showDisable && (
                      <form onSubmit={disable} className="mt-4 grid gap-3" data-testid="sec-disable-form">
                        <PwdInput value={disablePwd} onChange={setDisablePwd} />
                        <CodeInput value={disableCode} onChange={setDisableCode} />
                        <div className="flex items-center gap-2 justify-end">
                          <button type="button" onClick={() => { setShowDisable(false); setDisablePwd(""); setDisableCode(""); setErr(""); }}
                                  className="btn-ghost px-4 py-2 rounded-full text-[12px]">Annuler</button>
                          <button type="submit" disabled={busy || !disablePwd || !disableCode}
                                  data-testid="sec-disable-submit"
                                  className="px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5 bg-[rgba(255,120,120,0.18)] border border-[rgba(255,120,120,0.4)] text-[#FFB4B4] hover:bg-[rgba(255,120,120,0.28)] disabled:opacity-60">
                            {busy && <Loader2 size={12} className="animate-spin" />}
                            Désactiver le 2FA
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function ErrBox({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-[rgba(255,120,120,0.35)] bg-[rgba(255,120,120,0.08)] px-4 py-3 text-[13px] text-[#FFB4B4]"
         data-testid="sec-error">
      <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{children}</span>
    </div>
  );
}

function PwdInput({ value, onChange }) {
  return (
    <div>
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[#0369A1] mb-2">Mot de passe actuel</span>
      <input type="password" autoComplete="current-password" data-testid="sec-input-password"
             className="mc-input w-full" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function CodeInput({ value, onChange }) {
  return (
    <div>
      <span className="block text-[11px] uppercase tracking-[0.16em] text-[#0369A1] mb-2">Code 2FA (6 chiffres ou code de récupération)</span>
      <input type="text" data-testid="sec-input-code"
             className="mc-input w-full font-mono tracking-[0.3em]"
             maxLength={20} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
    </div>
  );
}

function downloadTxt(filename, content) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
}
